/* @revflare
  {
      "id": "revflare-cart",
      "name": "Revflare Cart",
      "version": "1.7.0",
      "description": "Cart drawer logic \u2014 open/close, line item updates, upsell injection, and AJAX cart API."
  }
*/
(function () {
  // === Remove initial-hide so drawer can animate normally after DOM is ready
  document.addEventListener("DOMContentLoaded", () => {
    const cart = document.getElementById('ufl-cart');
    if (cart) cart.classList.remove('ufl-initial-hide');
    // remove the critical first-paint guard once JS is live
    const critical = document.getElementById('ufl-cart-critical');
    if (critical) critical.remove();
  });

  const LOG_PREFIX = '[UFL CART]';
  const DEBUG = document.body.hasAttribute('data-ufl-debug');
  const debugLog = (...args) => DEBUG && console.log(LOG_PREFIX, ...args);

  // === Accessibility Announcer
  function announce(message) {
    const announcer = document.getElementById("ufl-cart-announcer");
    if (announcer) announcer.textContent = message;
  }

  // === Drawer Controls
  let lastFocusedElement = null;
  let openCartDrawer = () => {
    const cart = document.getElementById("ufl-cart");
    if (cart) {
      lastFocusedElement = document.activeElement;
      // ensure cart can paint and interact
      cart.style.visibility = 'visible';
      cart.removeAttribute('inert');

      cart.classList.remove("ufl-cart-hidden");
      cart.setAttribute("tabindex", "-1");
      cart.focus();
      trapFocus(cart);
      debugLog("Opened UFL cart drawer");
    }
    clearThemeDrawerArtifacts();
  }

  let focusTrapHandler = null;
  function trapFocus(container) {
    focusTrapHandler = (e) => {
      const focusableElements = container.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
      const firstEl = focusableElements[0];
      const lastEl = focusableElements[focusableElements.length - 1];

      if (e.key === "Tab") {
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener("keydown", focusTrapHandler);
  }
  function removeTrapFocus() {
    document.removeEventListener("keydown", focusTrapHandler);
  }

  window.uflCloseCart = () => {
    const cart = document.getElementById("ufl-cart");
    if (cart) {
      cart.classList.add("ufl-cart-hidden");

      // keep cart non-interactive and visually hidden between opens
      cart.setAttribute('inert', '');
      cart.style.visibility = 'hidden';

      removeTrapFocus();
    }
    if (lastFocusedElement) lastFocusedElement.focus();
  };

  function clearThemeDrawerArtifacts() {
    document.body.classList.remove(
      "js-drawer-open", "cart-drawer-open", "drawer-open", "overflow-hidden",
      "cart-open", "open", "is-active", "lock-scroll"
    );
    document.body.style.cssText = '';
    document.querySelectorAll(
      "#CartDrawer, .cart-drawer, [data-cart-drawer], .cart-drawer__overlay, .drawer__overlay, .modal-backdrop, .bg-overlay, .overlay, .drawer__underlay"
    ).forEach(el => el.remove());
  }

  // === Update Cart Attributes Helper
  async function updateCartAttributes(cart) {
    try {
      const upsellItems = [];
      let upsellCheckboxValue = 0;
      let upsellRowValue = 0;
      let upsellPdpListValue = 0;

      cart.items.forEach(item => {
        const upsellType = item.properties?._ufl_upsell;
        if (upsellType) {
          const upsellObj = {
            price: item.price,
            product_id: item.product_id,
            variant_id: item.variant_id,
            upsell: upsellType
          };
          upsellItems.push(upsellObj);

          if (upsellType === 'checkbox') upsellCheckboxValue += item.line_price;
          if (upsellType === 'row') upsellRowValue += item.line_price;
          if (upsellType === 'pdp-list') upsellPdpListValue += item.line_price;
        }
      });

      const attributesPayload = {
        attributes: {
          ufl_upsell_checkbox: upsellCheckboxValue,
          ufl_upsell_row: upsellRowValue,
          ufl_upsell_pdp_list: upsellPdpListValue,
          ufl_upsell_total: upsellCheckboxValue + upsellRowValue + upsellPdpListValue,
          ufl_cart_value: cart.total_price,
          ufl_currency: cart.currency,
          ufl_upsell_items: JSON.stringify(upsellItems)
        }
      };

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-UFL-Internal': 'true'
        },
        body: JSON.stringify(attributesPayload)
      });
      debugLog('Cart attributes updated', attributesPayload);
    } catch (err) {
      debugLog('Failed to update cart attributes', err);
    }
  }

  // === Checkout Updating Helper
  function setCheckoutLoading(isLoading) {
    const checkoutBtns = document.querySelectorAll('.ufl-cart-checkout');

    checkoutBtns.forEach(btn => {
      if (isLoading) {
        btn.disabled = true;
        btn.classList.add('loading');
      } else {
        btn.disabled = false;
        btn.classList.remove('loading');
      }
    });
  }

  // === Theme Editor Cart Content Check
  function isThemeEditorPreview() {
    return window.Shopify && window.Shopify.designMode === true;
  }

  // === Sync checkbox upsell qty (qty, value-primary-currency, value-any-currency)
  async function syncCheckboxUpsells(cart) {
    try {
      const cartEl = document.getElementById('ufl-cart');
      const shopCurrency = cartEl?.dataset.shopCurrency;
      const userCurrency = cartEl?.dataset.userCurrency;
      const fxRate = window.Shopify?.currency?.rate || 1;

      // Only count main/row items so upsells don't inflate themselves
      const mainAndRowItems = cart.items.filter(i => i.properties?._ufl_upsell !== 'checkbox');
      const checkboxItems = cart.items.filter(i => i.properties?._ufl_upsell === 'checkbox');

      // 1) For multiplier='qty'
      const baseQty = mainAndRowItems.reduce((sum, i) => sum + (i.quantity || 0), 0);

      // 2) For multiplier='value*' — spend of main/row items in presentment cents
      const presentmentSpendCents = mainAndRowItems.reduce((sum, i) => sum + (i.line_price || 0), 0);

      // Helpers for spend in store cents
      const asStoreCentsStrict = () => {
        if (userCurrency !== shopCurrency) return null;                         // mismatch => strict mode off
        return presentmentSpendCents;                                           // same currency, cents align
      };
      const asStoreCentsApprox = () => Math.round(presentmentSpendCents / (fxRate || 1));

      const updates = {};

      document.querySelectorAll('.ufl-cart-toggle[data-ufl-upsell-type="checkbox"]').forEach(toggle => {
        const variantId = parseInt(toggle.dataset.variantId, 10);
        if (!variantId) return;

        const li = checkboxItems.find(i => i.variant_id === variantId);
        const currentQty = li ? li.quantity : 0;
        const mult = (toggle.dataset.uflMultiplier || '').toLowerCase();

        // Disabled toggle → ensure 0
        if (!toggle.checked) {
          if (currentQty !== 0) updates[variantId] = 0;
          return;
        }

        if (mult === 'qty') {
          if (currentQty !== baseQty) updates[variantId] = baseQty;
          return;
        }

        if (mult === 'value-primary-currency' || mult === 'value-all-currencies') {
          const step = parseFloat(toggle.dataset.uflMultiplierValue || '0'); // e.g. 50
          if (!step || step <= 0) return;

          const storeCents =
            mult === 'value-primary-currency'
              ? asStoreCentsStrict()
              : asStoreCentsApprox();

          if (storeCents == null) {
            // strict mode but currencies differ → do nothing (keeps currentQty)
            return;
          }

          const stepCents = Math.round(step * 100);
          const targetQty = Math.max(1, Math.ceil(storeCents / stepCents));

          if (currentQty !== targetQty) updates[variantId] = targetQty;
          return;
        }
      });

      if (!Object.keys(updates).length) return;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-UFL-Internal': 'true'
        },
        body: JSON.stringify({ updates })
      });

      await refreshCartElements(false, true);
    } catch (err) {
      debugLog('Failed to sync checkbox upsell quantities', err);
    }
  }

  // === Dynamic Refresh
  async function refreshCartElements(suppressOpen = false, isRetry = false) {
    try {
      // Fetch JSON (for count) and HTML (for Liquid-rendered cart)
      const [cartRes, htmlRes] = await Promise.all([
        fetch("/cart.js").then(r => r.json()),
        fetch(window.location.href).then(r => r.text())
      ]);

      // Separate items into main/row and checkbox upsells
      const mainAndRowItems = cartRes.items.filter(item => item.properties?._ufl_upsell !== 'checkbox');
      const checkboxItems = cartRes.items.filter(item => item.properties?._ufl_upsell === 'checkbox');

      // If only checkbox upsells are left in the cart, remove them to prevent an orphaned state.
      if (mainAndRowItems.length === 0 && checkboxItems.length > 0 && !isRetry) {
        const removalPromises = checkboxItems.map(item =>
          fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-UFL-Internal': 'true'
            },
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          })
        );

        await Promise.all(removalPromises);

        // Refresh again to get the final empty cart state, preventing an infinite loop.
        return refreshCartElements(suppressOpen, true);
      }
      // Detect empty BEFORE update
      const wasEmptyUI = document.querySelector('.ufl-cart-empty') !== null;

      const htmlDoc = new DOMParser().parseFromString(htmlRes, 'text/html');
      const newCartContent = htmlDoc.querySelector('#ufl-cart .ufl-cart-content');

      // Get wrapper and check if it was hidden BEFORE update
      const cartWrapper = document.getElementById('ufl-cart');
      const wasHidden = cartWrapper.classList.contains('ufl-cart-hidden');

      // Replace only the inner content, not the wrapper
      const cartContentContainer = document.querySelector('#ufl-cart .ufl-cart-content');
      if (!cartContentContainer || !newCartContent) return;
      cartContentContainer.innerHTML = newCartContent.innerHTML;

      // Refresh promo modal content too
      const newPromoModal = htmlDoc.querySelector('#ufl-cart #ufl-promo-modal');
      const promoModalContainer = document.querySelector('#ufl-cart #ufl-promo-modal');
      if (promoModalContainer && newPromoModal) {
        promoModalContainer.innerHTML = newPromoModal.innerHTML;
      }

      // Auto-open if cart was hidden and now has items
      if (!suppressOpen && wasHidden && cartRes.item_count > 0) {
        openCartDrawer();
      }

      // Rebind events (quantity, notes, checkout hijacks)
      attachQuantityHandlers();
      attachCartNoteHandler();
      attachDiscountHandler();
      hijackCheckoutButtons();
      attachUpsellQuickAdd();
      attachUpsellToggle();
      attachPromoModalHandlers();

      // Run autoAdd only if cart transitioned empty → not empty
      const nowNotEmpty = cartRes.item_count > 0;
      if (wasEmptyUI && nowNotEmpty) {
        autoAddOptoutCheckboxes();
      }

      // Update cart count in header
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cartRes.item_count > 0 ? `(${cartRes.item_count})` : '';
      });

      if (cartRes.item_count === 0) {
        localStorage.removeItem('uflCartCountdown1');
        localStorage.removeItem('uflCartCountdown2');
      }
      initCartCountdown();
      await syncCheckboxUpsells(cartRes);
      updateCartAttributes(cartRes);
      setCheckoutLoading(false);

    } catch (err) {
      debugLog("Cart refresh failed", err);
    }
  }

  // === Line item qty buttons
  function attachQuantityHandlers() {
    document.querySelectorAll('.ufl-qty-plus, .ufl-qty-minus').forEach(btn => {
      if (btn.dataset.uflBound) return;
      btn.dataset.uflBound = "true";

      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();

        const cartItem = btn.closest('[data-line-item-key]');
        if (!cartItem) return;

        const lineItemKey = cartItem.getAttribute('data-line-item-key');
        const quantityDisplay = cartItem.querySelector('.ufl-qty-select span');
        let currentQty = parseInt(quantityDisplay.textContent, 10) || 1;

        btn.disabled = true;
        quantityDisplay.classList.add('loading');

        if (btn.classList.contains('ufl-qty-plus')) {
          currentQty++;
        } else if (btn.classList.contains('ufl-qty-minus')) {
          currentQty--;
        }

        if (currentQty < 0) currentQty = 0;
        setCheckoutLoading(true);
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-UFL-Internal': 'true'
            },
            body: JSON.stringify({ id: lineItemKey, quantity: currentQty })
          });
          refreshCartElements(false);
          announce(`Quantity updated to ${currentQty}`);
        } catch (err) {
          debugLog('Quantity update failed', err);
          quantityDisplay.classList.remove('loading');
          btn.disabled = false;
        }
      });
    });
  }

  // === Upsell Row Quick Add
  function attachUpsellQuickAdd() {
    document.querySelectorAll('.ufl-cart-upsell-add').forEach(btn => {
      if (btn.dataset.uflBound) return;
      btn.dataset.uflBound = "true";

      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();

        const productId = btn.getAttribute('data-variant-id');
        if (!productId) return;

        // Show loading spinner on button
        btn.disabled = true;
        btn.classList.add('loading');
        setCheckoutLoading(true);

        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-UFL-Internal': 'true'
            },
            body: JSON.stringify({ id: productId, quantity: 1, properties: { _ufl_upsell: 'row' } })
          });

          // Refresh cart content
          await refreshCartElements(false);

          // ACCESSIBILITY: Announce addition
          const productTitle = btn.closest('.ufl-cart-upsell')?.querySelector('p')?.textContent || 'Item';
          announce(`${productTitle} added to cart`);

        } catch (err) {
          console.error('[UFL CART] Upsell quick add failed', err);
        } finally {
          // Restore button state
          btn.disabled = false;
          btn.classList.remove('loading');
        }
      });
    });
  }

  // === Upsell Checkbox
  function attachUpsellToggle() {
    document.querySelectorAll('.ufl-cart-toggle').forEach(toggle => {
      if (toggle.dataset.uflBound) return;
      toggle.dataset.uflBound = 'true';

      toggle.addEventListener('change', async () => {
        const variantId = toggle.dataset.variantId;
        setCheckoutLoading(true);

        if (toggle.checked) {
          // Add item
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-UFL-Internal': 'true'
            },
            body: JSON.stringify({ id: variantId, quantity: 1, properties: { _ufl_upsell: 'checkbox' } })
          });
          announce(`${toggle.getAttribute('aria-label')} added to cart`);
        } else {
          // Remove item (set quantity to 0)
          const cart = await fetch('/cart.js').then(r => r.json());
          const lineItem = cart.items.find(i => i.variant_id == variantId);
          if (lineItem) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-UFL-Internal': 'true'
              },
              body: JSON.stringify({ id: lineItem.key, quantity: 0 })
            });
            announce(`${toggle.getAttribute('aria-label')} removed from cart`);
          }
        }

        refreshCartElements(false).then(async () => {
          const cart = await fetch('/cart.js').then(r => r.json());
          await syncCheckboxUpsells(cart);
        });
      });
    });
  }

  // === Upsell Checkbox: Optout
  async function autoAddOptoutCheckboxes(cartItems) {
    const optoutCheckboxes = document.querySelectorAll('.ufl-cart-toggle[data-optout="true"]');
    if (!optoutCheckboxes.length) return;

    // Fetch cart to check existing items
    const cart = await fetch('/cart.js').then(r => r.json());

    const optoutIds = Array.from(optoutCheckboxes).map(el => parseInt(el.dataset.variantId, 10));

    for (const variantId of optoutIds) {
      // Only add if not already in cart
      if (cart.items.some(i => i.variant_id === variantId)) continue;

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-UFL-Internal': 'true'
        },
        body: JSON.stringify({ id: variantId, quantity: 1, properties: { _ufl_upsell: 'checkbox' } })
      });
    }

    refreshCartElements(false);
  }

  // === Timer
  function initCartCountdown() {
    const timers = document.querySelectorAll('[data-ufl-timer]');
    if (!timers.length) return;

    timers.forEach(timerElement => {
      const duration = parseInt(timerElement.dataset.uflTimer, 10);
      if (!duration) return;

      const timerId = timerElement.dataset.timerId || '1';
      const STORAGE_KEY = `uflCartCountdown${timerId}`;

      let endTime = parseInt(localStorage.getItem(STORAGE_KEY), 10);

      // If no timer exists OR expired, initialize fresh
      if (!endTime || endTime < Date.now()) {
        endTime = Date.now() + duration * 1000;
        localStorage.setItem(STORAGE_KEY, endTime);
      }

      function formatTime(seconds) {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      }

      function tick() {
        let remaining = Math.floor((endTime - Date.now()) / 1000);

        // Restart when expired
        if (remaining <= 0) {
          endTime = Date.now() + duration * 1000;
          localStorage.setItem(STORAGE_KEY, endTime);
          remaining = duration;
        }

        timerElement.textContent = formatTime(remaining);
      }

      tick();
      setInterval(tick, 1000);
    });
  }

  // === Promotion Modal
  function openPromoModal() {
    const modal = document.querySelector('#ufl-cart #ufl-promo-modal');
    if (modal) modal.classList.add('active');
  }
  function closePromoModal() {
    const modal = document.querySelector('#ufl-cart #ufl-promo-modal');
    if (modal) modal.classList.remove('active');
  }
  // Reusable attach function (call on load + after cart refresh)
  function attachPromoModalHandlers() {
    document.querySelectorAll('.ufl-cart-promo-open').forEach(btn => {
      btn.removeEventListener('click', openPromoModalHandler);
      btn.addEventListener('click', openPromoModalHandler);
    });

    document.querySelectorAll('#ufl-promo-modal .ufl-promo-close').forEach(btn => {
      btn.removeEventListener('click', closePromoModalHandler);
      btn.addEventListener('click', closePromoModalHandler);
    });

    document.querySelectorAll('#ufl-promo-modal .ufl-promo-backdrop').forEach(backdrop => {
      backdrop.removeEventListener('click', closePromoModalHandler);
      backdrop.addEventListener('click', closePromoModalHandler);
    });
  }
  function openPromoModalHandler(e) {
    e.preventDefault();
    openPromoModal();
  }
  function closePromoModalHandler(e) {
    e.preventDefault();
    closePromoModal();
  }

  // === Discount code input: adding and removing a discount code
  function attachDiscountHandler() {
    const form = document.querySelector('#ufl-cart-discount-form');
    const input = document.querySelector('#ufl-cart-discount-input');

    if (!form || !input) return;
    if (form.dataset.uflBound) return;
    form.dataset.uflBound = "true";

    // === Handle applying discount via form submit
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const code = input.value.trim();
      if (!code) return;

      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-UFL-Internal': 'true'
          },
          body: JSON.stringify({ discount: code })
        });

        // Refresh UI after applying discount
        refreshCartElements(false);
      } catch (err) {
        console.error('[UFL CART] Failed to apply discount code', err);
        showDiscountError();
      }
    });

    // === Handle removing discount codes (pill "X")
    document.querySelectorAll('.ufl-cart-discount-remove').forEach(btn => {
      if (btn.dataset.uflBound) return;
      btn.dataset.uflBound = "true";

      btn.addEventListener('click', async () => {
        const code = btn.closest('.ufl-cart-discount-pill')?.dataset.discountCode;
        if (!code) return;

        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-UFL-Internal': 'true'
            },
            body: JSON.stringify({ discount: '' }) // Clear all discounts
          });

          refreshCartElements(false);
        } catch (err) {
          console.error('[UFL CART] Failed to remove discount code', err);
        }
      });
    });

    function showDiscountError() {
      const errorEl = document.querySelector('.ufl-cart-discount-error');
      if (errorEl) errorEl.classList.remove('hidden');
    }
  }

  // === Cart note
  function attachCartNoteHandler() {
    const noteInput = document.querySelector('#ufl-cart-note-input');
    if (!noteInput) return;
    if (noteInput.dataset.uflBound) return;
    noteInput.dataset.uflBound = "true";

    // Save on blur
    noteInput.addEventListener('blur', saveNote);

    // Save on checkout click
    document.querySelectorAll('[href="/checkout"], [name="checkout"]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const note = noteInput.value.trim();
        if (note) {
          // Save note before navigating
          e.preventDefault();
          await saveNote();
          window.location.href = "/checkout";
        }
      });
    });

    async function saveNote() {
      const note = noteInput.value.trim();
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-UFL-Internal': 'true'
          },
          body: JSON.stringify({ note })
        });
      } catch (err) {
        debugLog('Failed to save note', err);
      }
    }
  }

  // === Hijack all ATC forms
  function hijackCartForms() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
      if (form.dataset.uflBound) return;
      form.dataset.uflBound = "true";

      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        const formData = new FormData(form);
        await fetch("/cart/add.js", { method: "POST", body: formData });
        refreshCartElements(false);
      });
    });
  }

  // === Hijack non-form ATC buttons broadly
  function hijackATCButtons() {
    const selectors = [
      // Existing
      '[data-add-to-cart]', '[data-smartrr-confirmation-cta]', '[data-smartrr-ccta-text]',
      '.btn--add-to-cart', '.product--add-to-cart-button', '[data-button-price]', '[data-ocupsell-add]',

      // attribute-based
      '[name="add"]', '[data-product-add]', '[data-action="add-to-cart"]',
      '[data-cart-button]', '[data-atc]', '[data-ajax-cart-submit]', '[data-freq-cart-add]',

      // class-based
      '.add-to-cart', '.product-form__cart-submit', '.atc-button', '.atc-btn',
      '.product__add-to-cart', '.ajax-submit', '.upsell-add',
      '.slidecart__button--atc', '.monster-cart-atc',

      // id-based
      '#AddToCart', '#product-add-to-cart', '#ajaxAddToCart'
    ];

    document.querySelectorAll(selectors.join(',')).forEach(btn => {
      if (btn.dataset.uflBound) return;
      btn.dataset.uflBound = "true";

      btn.addEventListener("click", async (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        debugLog("Hijacked direct ATC button click");

        // Fallback: try to find form and submit via /cart/add.js
        const form = btn.closest('form[action*="/cart/add"]');
        if (form) {
          const formData = new FormData(form);
          await fetch("/cart/add.js", { method: "POST", body: formData });
          refreshCartElements(false);
        } else {
          debugLog("No ATC form found to hijack button click");
        }
      });
    });
  }

  // === Hijack cart icon buttons
  function hijackCartLinks() {
    document.querySelectorAll('a[href$="/cart"], button[aria-controls*="cart"]').forEach(el => {
      if (el.dataset.uflBound) return;
      el.dataset.uflBound = "true";
      el.removeAttribute("aria-controls");
      el.removeAttribute("aria-expanded");
      el.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        debugLog("Hijacked cart icon link");
        openCartDrawer();
      });
    });
  }

  // === Hijack checkout
  function hijackCheckoutButtons() {
    document.querySelectorAll('[href="/checkout"], [name="checkout"]').forEach(btn => {
      if (btn.dataset.uflBound) return;
      btn.dataset.uflBound = "true";
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = "/checkout";
      });
    });
  }

  // === Prevent aria-expanded from toggling
  function enforceAriaExpandedFalse() {
    document.querySelectorAll('[aria-controls*="cart"]').forEach(el => {
      new MutationObserver(muts => {
        muts.forEach(m => {
          if (m.attributeName === 'aria-expanded') {
            el.setAttribute('aria-expanded', 'false');
            debugLog("Forced aria-expanded=false");
          }
        });
      }).observe(el, { attributes: true });
    });
  }

  // === Suppress OCU, UpCart, SlideCart etc.
  function suppressThirdPartyCarts() {
    const selectors = [
      // OCU
      '#ocu-cart-drawer', '[id*="ocu-"]', '[class*="ocu-"]', '.oneclickupsell', '[data-app="ocu"]',
      'iframe[src*="oneclickupsell"]',

      // UpCart
      '#upcart-drawer', '[id*="upcart"]', '[class*="upcart"]',
      '[data-upcart]', '[data-app="upcart"]',
      'iframe[src*="upcart"]'
    ];

    selectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(el => {
        el.remove();
        debugLog("Removed 3rd party cart element:", el);
      });
    });

    // Globals
    if (window.oneClickUpsell) {
      window.oneClickUpsell = null;
      debugLog("Suppressed OCU global");
    }
    if (window.UpCart) {
      window.UpCart = null;
      debugLog("Suppressed UpCart global");
    }
  }

  // === Sweep overlays + OCU continuously
  function startSweeper() {
    function sweep() {
      clearThemeDrawerArtifacts();
      suppressThirdPartyCarts();
      requestAnimationFrame(sweep);
    }
    requestAnimationFrame(sweep);
  }

  // === Monkey-patch theme + OCU event listeners
  function monkeyPatchAddEventListener() {
    const original = EventTarget.prototype.addEventListener;
    EventTarget.prototype.addEventListener = function (type, listener, options) {
      // Only run block logic if 'this' is an Element
      if (
        this instanceof Element &&
        (
          this.matches('[aria-controls*="cart"], a[href$="/cart"]') ||
          this.matches('[class*="ocu"], [id*="ocu"], [data-app="ocu"]') ||
          this.matches('[class*="upcart"], [id*="upcart"], [data-app="upcart"], [data-upcart]') ||
          this.matches('[name="add"], .add-to-cart, .product-form__cart-submit, [data-add-to-cart]')
        ) &&
        (type === "click" || type === "submit" || type === "keydown")
      ) {
        debugLog("Blocked unwanted cart event listener");
        return;
      }
      return original.call(this, type, listener, options);
    };
  }

  // === Kill Shopify Cart Callbacks
  function nullifyShopifyEvents() {
    if (window.Shopify) {
      Shopify.onItemAdded = () => { };
      Shopify.onCartUpdate = () => { };
      Shopify.onCartChanged = () => { };
      debugLog("Suppressed Shopify cart callbacks");
    }
  }

  // === Block cart-related custom events
  function suppressCustomEvents() {
    ['cart:updated', 'cart:opened', 'cart:closed'].forEach(evt => {
      window.addEventListener(evt, e => {
        e.stopImmediatePropagation();
        debugLog(`Blocked custom event: ${evt}`);
      }, true);
    });
  }

  // === Global fallback click handler
  function globalCartTriggerListener(e) {
    const selectors = [
      '[href$="/cart"]', '[aria-controls*="cart"]', '[data-action="open-cart"]',
      '[data-cart-trigger]', '[data-drawer-id="cart"]',
      '.cart-toggle', '.cart-link', '.header__icon--cart', '.drawer-cart-toggle',
      '[data-drawer-open="cart"]',

      // UpCart
      '[data-upcart-toggle]', '.upcart-toggle', '.upcart-open', '.upcart-drawer-toggle'
    ];
    const target = e.target.closest(selectors.join(','));
    if (target) {
      e.preventDefault();
      e.stopPropagation();
      debugLog("Global fallback triggered cart open");
      openCartDrawer();
    }
  }

  // === DOM Watcher
  function observeDOM() {
    new MutationObserver(() => {
      hijackCartForms();
      hijackATCButtons();
      hijackCartLinks();
      hijackCheckoutButtons();
      enforceAriaExpandedFalse();
      suppressThirdPartyCarts();
      attachQuantityHandlers();
      attachCartNoteHandler();
      attachDiscountHandler();
    }).observe(document.body, { childList: true, subtree: true });
  }

  // === Universal cart change detector (fetch & XHR override)
  const originalFetch = window.fetch;
  window.fetch = async function (resource, init) {
    const url = typeof resource === "string" ? resource : resource.url || "";

    // Check for our internal header
    const isInternal = init?.headers && (
      init.headers['X-UFL-Internal'] === 'true' ||
      (init.headers.get && init.headers.get('X-UFL-Internal') === 'true')
    );

    const res = await originalFetch(resource, init);
    // Only refresh if not internal
    if (!isInternal) {
      if (url.includes("/cart/add")) {
        refreshCartElements(false);
      } else if (url.includes("/cart/change") || url.includes("/cart/update")) {
        refreshCartElements(true);
      }
    }

    return res;
  };

  const originalXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    // We check headers when `send()` is called
    this._isUflInternal = false;

    const originalSetRequestHeader = this.setRequestHeader;
    this.setRequestHeader = function (header, value) {
      if (header === 'X-UFL-Internal' && value === 'true') {
        this._isUflInternal = true;
      }
      originalSetRequestHeader.call(this, header, value);
    };

    this.addEventListener("load", () => {
      if (this._isUflInternal) return;
      if (url.includes("/cart/add")) {
        refreshCartElements(false);
      } else if (url.includes("/cart/change") || url.includes("/cart/update")) {
        refreshCartElements(true);
      }
    });

    return originalXHROpen.apply(this, arguments);
  };

  // === Init
  document.addEventListener("DOMContentLoaded", () => {
    debugLog("Init");
    hijackCartForms();
    hijackATCButtons();
    hijackCartLinks();
    hijackCheckoutButtons();
    attachUpsellQuickAdd();
    attachUpsellToggle();
    attachPromoModalHandlers();
    observeDOM();
    nullifyShopifyEvents();
    enforceAriaExpandedFalse();
    startSweeper();
    suppressCustomEvents();
    setTimeout(monkeyPatchAddEventListener, 0);
    document.addEventListener("click", globalCartTriggerListener, true);
    suppressThirdPartyCarts(); // Initial nuke
    initCartCountdown();
  });

  // === Theme Editor Check
  document.addEventListener("DOMContentLoaded", () => {
    if (isThemeEditorPreview()) {
      // Always refresh cart state on load (fix empty cart issue)
      refreshCartElements(true);

      // Re-run refresh on Theme Editor partial reloads
      document.addEventListener('shopify:section:load', () => refreshCartElements(true));

      // Preserve existing drawer-open state handling
      const originalOpen = openCartDrawer;
      openCartDrawer = function () {
        sessionStorage.setItem('uflCartDrawerOpen', 'true');
        originalOpen.apply(this, arguments);
      };
      const originalClose = window.uflCloseCart;
      window.uflCloseCart = function () {
        sessionStorage.removeItem('uflCartDrawerOpen');
        originalClose.apply(this, arguments);
      };
    }
  });
})();

// === Accessibility: close using esc key
document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") {
    const cart = document.getElementById("ufl-cart");
    if (cart && !cart.classList.contains("ufl-cart-hidden")) {
      window.uflCloseCart();
    }
  }
});